import { useState } from 'react'
import { Box, Button, FormControl, FormLabel, Input, Text } from '@chakra-ui/react'
import InvalidInput from '../input/InvalidInput'

interface ChildFormValues {
  display_name: string
  date_of_birth: string | null
}

interface ChildFormProps {
  initialValues?: ChildFormValues
  onSubmit: (values: ChildFormValues) => void
  isSubmitting?: boolean
  submitLabel?: string
}

// Reusable, coupling-free two-field form for creating/editing a child profile.
// Owns ONLY: display_name (required), date_of_birth (optional), required-name
// validation, native date-only handling, and the submit button. It intentionally
// contains no mutation, cache, or modal-disclosure coupling and no react-query
// imports so Phase 15 can mount it verbatim inside the checkout modal (D-04).
const ChildForm = ({ initialValues, onSubmit, isSubmitting, submitLabel }: ChildFormProps) => {
  const [displayName, setDisplayName] = useState<string>(initialValues?.display_name ?? '')
  // DOB stays a raw 'YYYY-MM-DD' string end-to-end — never a Date, never a UTC ISO round-trip (D-08, FAM-06).
  const [dob, setDob] = useState<string>(initialValues?.date_of_birth ?? '')
  const [nameError, setNameError] = useState(false)

  const handleSubmit = () => {
    const trimmedName = displayName.trim()
    if (trimmedName === '') {
      setNameError(true)
      return
    }
    setNameError(false)
    // Blank DOB submits as null and never gates the flow (D-09).
    onSubmit({ display_name: trimmedName, date_of_birth: dob === '' ? null : dob })
  }

  return (
    <Box>
      <FormControl>
        <FormLabel>Name</FormLabel>
        <Input
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value)
            if (nameError) setNameError(false)
          }}
        />
        <InvalidInput isError={nameError} override="Please enter a name" />
      </FormControl>

      <FormControl mt={4}>
        <FormLabel htmlFor="child-dob">
          Date of birth{' '}
          <Text as="span" variant="subtle" fontSize="sm">
            (Optional)
          </Text>
        </FormLabel>
        <Input id="child-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
      </FormControl>

      <Button
        w="100%"
        variant="heavy"
        mt={6}
        minH={11}
        isLoading={isSubmitting}
        onClick={handleSubmit}
      >
        {submitLabel ?? 'Save child'}
      </Button>
    </Box>
  )
}

export default ChildForm
export type { ChildFormValues, ChildFormProps }
